import { useKlussenGenerator } from "@personeel/hooks/useFetchData.ts";
import { formatTime, isValidTime } from "@personeel/utils/DateTimeUtils.ts";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore";
import { DATETIME_FORMAT_NO_SECONDS } from "@shared/common/utils/luxonHelpers";
import { useMessageBannerStore } from "@shared/shared-zustand/MessageBannerStore";
import { loadDefaultFeedbackMessages } from "@tractie/components";
import { DateTime } from "luxon";
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./GenerateKlussen.css";

export const KLUSSOORT_LOPEN = "LOPEN";

export function GenerateKlussen(): JSX.Element {
  const scenarioInterval = useSelectedTimeStore((state) => state.scenarioInterval)!;

  const scenarioStart = scenarioInterval?.start;
  const scenarioEnd = scenarioInterval?.end;

  const [errors, setErrors] = useState("");
  const isSubmittingRef = useRef(false);
  const [formState, setFormState] = useState({
    start: scenarioStart.toFormat("yyyy-MM-dd"),
    startTime: scenarioStart.toFormat("HH:mm"),
    end: scenarioEnd.toFormat("yyyy-MM-dd"),
    endTime: scenarioEnd.toFormat("HH:mm"),
  });

  const [isGenerating, setIsGenerating] = useState(false);

  const { mutate: generateKlussen } = useKlussenGenerator();

  const setMessageBanner = useMessageBannerStore((state) => state.setBanner);

  const updateFormState = useCallback((data: Partial<typeof formState>) => {
    setFormState((previousState) => {
      return {
        ...previousState,
        ...data,
      };
    });
  }, []);

  const isValidTimeInput = useCallback(
    (time: string, name: string): string => {
      const formattedTime = formatTime(time.trim());

      if (formattedTime === "" || !isValidTime(formattedTime)) {
        // markeer als invalid, maar laat validateInput bepalen welke error getoond wordt
        updateFormState({ [name]: time } satisfies Partial<typeof formState>);
        return "invalid";
      }

      updateFormState({ [name]: formattedTime } satisfies Partial<typeof formState>);
      return formattedTime;
    },
    [updateFormState],
  );

  const validateInput = useCallback(
    (allValues: Record<string, string>): string => {
      let errorMessage = "";

      if (Object.keys(allValues).length > 0) {
        const startDate = allValues["start"];
        const startTime = isValidTimeInput(allValues["startTime"], "startTime");
        const endDate = allValues["end"];
        const endTime = isValidTimeInput(allValues["endTime"], "endTime");

        if (startTime === "invalid" || endTime === "invalid") {
          errorMessage = "Ongeldige tijd ingevoerd.";
        } else {
          const selectedScenarioStartDateTime = DateTime.fromFormat(
            `${startDate} ${startTime}`,
            "yyyy-MM-dd HH:mm",
          );
          const selectedScenarioEndDateTime = DateTime.fromFormat(
            `${endDate} ${endTime}`,
            "yyyy-MM-dd HH:mm",
          );

          if (
            selectedScenarioStartDateTime < scenarioStart ||
            selectedScenarioEndDateTime > scenarioEnd
          ) {
            errorMessage =
              "De begindatum/tijd en de einddatum/tijd moeten binnen het scenariobereik vallen.";
          } else if (selectedScenarioStartDateTime > selectedScenarioEndDateTime) {
            errorMessage = "De startdatum/tijd moet vóór de einddatum/tijd liggen.";
          }
        }
      }

      setErrors(errorMessage);
      return errorMessage;
    },
    [scenarioEnd, scenarioStart, isValidTimeInput],
  );

  const generateKlussenHandler = useCallback(
    (event: CustomEvent<unknown>) => {
      event.stopPropagation();

      if (isSubmittingRef.current) {
        return;
      }
      const errorMessage = validateInput(formState);
      if (errorMessage) {
        console.error("Er zijn fouten in het formulier. Los deze op voordat je klussen genereert.");
        return;
      }

      const startDatum = DateTime.fromFormat(
        `${formState["start"]} ${formState["startTime"]}`,
        "yyyy-MM-dd HH:mm",
      );
      const eindDatum = DateTime.fromFormat(
        `${formState["end"]} ${formState["endTime"]}`,
        "yyyy-MM-dd HH:mm",
      );

      if (!startDatum.isValid || !eindDatum.isValid) {
        setErrors("Kon de ingevoerde datum/tijd niet verwerken.");
        return;
      }

      isSubmittingRef.current = true;
      setIsGenerating(true);
      generateKlussen(
        { startDatum: startDatum.toISO() ?? "", eindDatum: eindDatum.toISO() ?? "" },
        {
          onSuccess: (data) => {
            setMessageBanner(
              `Genereren van klussen is gereed. ${data?.filter((d) => d.klusSoort !== KLUSSOORT_LOPEN).length} klussen gegenereerd.`,
              "success",
            );
          },
          onError: () => {
            setMessageBanner(
              "Genereren van klussen is mislukt. Probeer het later opnieuw.",
              "error",
            );
          },
          onSettled: () => {
            isSubmittingRef.current = false;
            setIsGenerating(false);
          },
        },
      );

      (event.target as unknown as { close: () => void }).close();
    },
    [formState, generateKlussen, validateInput, setMessageBanner],
  );

  const cancelEventHandler = useCallback(
    (event: CustomEvent<unknown>) => {
      setErrors("");
      updateFormState({
        start: scenarioStart.toFormat("yyyy-MM-dd"),
        startTime: scenarioStart.toFormat("HH:mm"),
        end: scenarioEnd.toFormat("yyyy-MM-dd"),
        endTime: scenarioEnd.toFormat("HH:mm"),
      });

      (event.target as unknown as { close: () => void }).close();
    },
    [scenarioEnd, scenarioStart, updateFormState],
  );
  useEffect(() => {
    loadDefaultFeedbackMessages();
  }, []);

  // use to populate the date select options based on the scenario start and end dates
  const scenarioDateRange = useMemo(() => {
    const range: Record<string, string> = {};
    let current = scenarioStart;

    while (current <= scenarioEnd) {
      range[current.toFormat("yyyy-MM-dd")] = current.toLocaleString({
        weekday: "short",
        month: "2-digit",
        day: "2-digit",
      });
      current = current.plus({ days: 1 }).startOf("day");
    }

    return range;
  }, [scenarioStart, scenarioEnd]);

  const handleSelectedDateChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;

      setErrors("");
      const newState = {
        ...formState,
        [name]: value,
      };

      setFormState(newState);

      validateInput(newState);
    },
    [formState, validateInput],
  );

  return (
    <tractie-dialog
      data-testid="dialog-generate-klussen"
      heading={"Genereer klussen"}
      size={"m"}
      ondialog-close={cancelEventHandler}
      ondialog-cancel={cancelEventHandler}
      ondialog-submit={(event) => {
        if (!isGenerating) generateKlussenHandler(event);
      }}
      buttons={{
        cancel: {
          label: "Annuleren",
        },
        submit: {
          label: "Genereren",
        },
      }}
    >
      <tractie-button data-testid="generate-klussen-button" slot="invoker" level="secondary">
        <tractie-icon name="plus" />
        {isGenerating ? "Bezig met genereren..." : "Klussen genereren"}
      </tractie-button>

      {isGenerating ? (
        <div data-testid="state-generate-klussen-loading">Bezig met genereren...</div>
      ) : (
        <>
          <tractie-divider />
          <div className="kg-dialog-formgroup">
            Kies een periode binnen het scenario {" ("}
            {scenarioStart.toFormat(DATETIME_FORMAT_NO_SECONDS)}
            {" - "}
            {scenarioEnd.toFormat(DATETIME_FORMAT_NO_SECONDS)}
            {")"}
          </div>
          <tractie-form name={"kgDialoog"}>
            <form data-testid="form-generate-klussen" onChange={(e) => e.preventDefault()}>
              <div className="form-time-selector">
                <tractie-select
                  id={"start"}
                  label={"Start"}
                  className="kg-dialog-date"
                  modelValue={formState.start}
                  name="startouter"
                >
                  <select
                    data-testid="select-generate-start-dag"
                    name="start"
                    slot="input"
                    value={formState.start}
                    // tractie-select re-dispatched het event, komt met naam startouter in handler, wel met goede waarde
                    onChange={(e) =>
                      handleSelectedDateChange({
                        ...e,
                        target: {
                          ...e.target,
                          name: "start", //dit was startouter door tractie
                          value: e.currentTarget.value,
                        },
                      })
                    }
                  >
                    {Object.entries(scenarioDateRange)?.map(([dateValue, label]) => (
                      <option key={dateValue} value={dateValue}>
                        {label}
                      </option>
                    ))}
                  </select>
                </tractie-select>
                <tractie-input
                  data-testid="input-generate-start-tijd"
                  name="startTime"
                  id={"startTime"}
                  label=" "
                  modelValue={formState.startTime}
                  placeholder="00:00"
                  className="kg-dialog-date"
                  onChange={handleSelectedDateChange}
                />
                <tractie-select
                  id={"end"}
                  label={"Eind"}
                  className="kg-dialog-date"
                  modelValue={formState.end}
                  name="endouter"
                >
                  <select
                    data-testid="select-generate-eind-dag"
                    name="end"
                    slot="input"
                    value={formState.end}
                    // tractie-select re-dispatched het event, komt met naam startouter in handler, wel met goede waarde
                    onChange={(e) =>
                      handleSelectedDateChange({
                        ...e,
                        target: {
                          ...e.target,
                          name: "end", //dit was endouter door tractie
                          value: e.currentTarget.value,
                        },
                      })
                    }
                  >
                    {Object.entries(scenarioDateRange)?.map(([dateValue, label]) => (
                      <option key={dateValue} value={dateValue}>
                        {label}
                      </option>
                    ))}
                  </select>
                </tractie-select>
                <tractie-input
                  data-testid="input-generate-eind-tijd"
                  id={"endTime"}
                  name={"endTime"}
                  label={" "}
                  modelValue={formState.endTime}
                  placeholder="00:00"
                  className="kg-dialog-date"
                  onChange={handleSelectedDateChange}
                />
              </div>

              <div style={{ height: "50px" }}>
                {errors && (
                  <tractie-error-message
                    data-testid="error-generate-klussen"
                    style={{ paddingTop: "10px" }}
                  >
                    {errors}
                  </tractie-error-message>
                )}
              </div>
              <tractie-divider />
            </form>
          </tractie-form>
        </>
      )}
    </tractie-dialog>
  );
}
